import type { BlobLike } from "@pdfking/compatibility"
import type {
  Color,
  Distance,
  Pixel,
  Point,
  Rectangle,
  RGBColor,
  SpatialRectangle,
} from "@pdfking/math"
import type { PlotColor, PlotShape as WasmPlotShape } from "@pdfking/pdfium"
import type { PDFPlotFunction, PlotShape } from "@pdfking/renderer-api"
import type { AsyncSequence } from "@pdfking/tfunc"
import { BlobArray, BlobToUint8 } from "@pdfking/compatibility"
import { CreateColorConvert } from "@pdfking/math"
import { ProvisionWasm } from "@pdfking/pdfium"
import { CreateContext, PlotFunction } from "@pdfking/renderer-api"
import {
  FullMethodDispatcher,
  Items,
  IterableToArray,
  RecordMap,
  StringHash,
} from "@pdfking/tfunc"
import { AutoThrow } from "../utils/AutoThrow"

const ToPlotColor = CreateColorConvert<PlotColor>(
  c => ({ ...c, enum: "rgb" }),
  c => ({ ...c, enum: "cmyk" })
)

const ResolvePlotColor = (
  color_map: Record<string, Color>,
  id: string | undefined
): PlotColor => ToPlotColor(color_map[id as any] || TRANSPARENT_RGB)

const TRANSPARENT_RGB: PlotColor = {
  ...({ red: 0, green: 0, blue: 0, alpha: 0 } as RGBColor),
  enum: "rgb",
}

export const PDFPlot: PDFPlotFunction = (pages, partial) =>
  ProvisionWasm(async function* (api) {
    const context = CreateContext<Promise<BlobLike>>(partial)
    const to_px = (value: Distance): Pixel =>
      (context.unit_to_px * value) as Pixel
    const stroke_width = to_px(context.stroke_width)

    for (const { height: h, width: w, bleed: b, shapes } of pages) {
      const height = to_px(h)
      const width = to_px(w)
      const bleed = to_px(b)

      const get_xy = ({ x: px, y: py }: Point<Distance>): Point<Pixel> => ({
        x: (bleed + to_px(px)) as Pixel,
        y: (bleed - to_px(py) + height) as Pixel,
      })

      const get_size = (size: Rectangle<Distance>): Rectangle<Pixel> =>
        RecordMap(to_px, size)

      const get_measures = (
        r: SpatialRectangle<Distance>
      ): SpatialRectangle<Pixel> => {
        const { x, y } = get_xy(r)
        const { height, width } = get_size(r)
        return {
          height,
          width,
          x,
          y: (y - height) as Pixel,
        }
      }

      const media = await Promise.all(
        Items(context.file_map).map(async ([key, promise]) => ({
          bytes: await promise.then(BlobToUint8),
          id: await StringHash([key]),
        }))
      )

      const ctx: PdfiumContext = {
        color_map: context.color_map,
        get_measures,
        get_xy,
        stroke_width,
      }

      const r = {
        page: {
          width: (width + bleed * 2) as Pixel,
          height: (height + bleed * 2) as Pixel,
          trim_left: bleed,
          trim_bottom: bleed,
          trim_right: (bleed + width) as Pixel,
          trim_top: (bleed + height) as Pixel,
        },
        shapes: await IterableToArray(...shapes.map(s => convertShape(s, ctx))),
        media,
      }

      yield BlobArray(AutoThrow(await api.plot(r)), "application/pdf")
    }
  })

type PdfiumContext = {
  color_map: Record<string, Color>
  get_measures: (r: SpatialRectangle<Distance>) => SpatialRectangle<Pixel>
  get_xy: (p: Point<Distance>) => Point<Pixel>
  stroke_width: Pixel
}

const color = (ctx: PdfiumContext, id: string | undefined): PlotColor =>
  ResolvePlotColor(ctx.color_map, id)

const convertShape = FullMethodDispatcher<
  [PlotShape, PdfiumContext],
  AsyncSequence<WasmPlotShape>
>({
  [PlotFunction.PLOT_RECT]: async function* (r, ctx) {
    yield {
      enum: PlotFunction.PLOT_RECT,
      ...ctx.get_measures(r.position),
      rotation: r.rotation,
      fill: color(ctx, r.fill_color),
      stroke: color(ctx, r.stroke_color),
      stroke_width: ctx.stroke_width,
    }
  },

  [PlotFunction.PLOT_TEXT]: async function* (t, ctx) {
    if (t.fill_color || t.stroke_color) {
      yield {
        enum: PlotFunction.PLOT_RECT,
        ...ctx.get_measures(t.position),
        rotation: t.rotation,
        fill: color(ctx, t.fill_color),
        stroke: color(ctx, t.stroke_color),
        stroke_width: ctx.stroke_width,
      }
    }

    const c = color(ctx, t.text_color)
    if (c.alpha > 0 && t.text) {
      yield {
        enum: PlotFunction.PLOT_TEXT,
        text: t.text,
        font_name: t.font_name,
        font_size: t.font_size,
        ...ctx.get_measures(t.position),
        horizontal_align: t.horizontal_align,
        vertical_align: t.vertical_align,
        color: c,
        rotation: t.rotation,
      }
    }
  },

  [PlotFunction.PLOT_PATH]: async function* (p, ctx) {
    yield {
      enum: PlotFunction.PLOT_PATH,
      points: p.points.map(pt => ctx.get_xy(pt)),
      closed: p.closed,
      fill: color(ctx, p.fill_color),
      stroke: color(ctx, p.stroke_color),
      stroke_width: ctx.stroke_width,
    }
  },

  [PlotFunction.PLOT_MEDIA]: async function* (m, ctx) {
    yield {
      enum: PlotFunction.PLOT_MEDIA,
      crop: ctx.get_measures(m.crop_position),
      media: ctx.get_measures(m.media_position),
      media_index: await StringHash([m.page]),
      rotation: m.rotation,
      scale: m.scale,
    }
  },
})
